/**
 * Notice that the worker stopped answering.
 *
 * Wall-clock time is no evidence that the page was running, so a frozen tab, a blocked main thread
 * or a throttled background timer all look like a dead worker if you only measure elapsed time.
 * This counts watchdog ticks instead: every tick is proof that the page ran, and browsers do not
 * queue up the ticks a frozen page missed.
 */
import {report_error, report_info} from "@cling/lib.shared.debug"
import {safe_session_storage} from "@cling/lib.web.utils/safe_storage"

/**
 * Ticks happen every 10s. Unpublished work makes a dead worker urgent, so we ask after half a
 * minute of a visible, running page. With nothing pending only incoming updates are missing, and
 * that can wait three minutes.
 */
export const MISSED_TICKS_WITH_PENDING_PATCHES = 3
export const MISSED_TICKS_UNTIL_UNAVAILABLE = 18
const RELOAD_BUDGET = 2
const RELOAD_BUDGET_WINDOW = 10 * 60_000
const RELOAD_BUDGET_KEY = "cling.worker_reloads"

type SessionStorage = Pick<Storage, "getItem" | "setItem">

/** How many of the recent reloads still count. Anything we cannot read counts as none. */
function reloads_within_window(storage: SessionStorage, now: number): number {
    try {
        const {count, at} = JSON.parse(storage.getItem(RELOAD_BUDGET_KEY) ?? "")
        if (
            typeof count === "number" &&
            typeof at === "number" &&
            now - at <= RELOAD_BUDGET_WINDOW
        ) {
            return count
        }
    } catch {
        // Ignored.
    }
    return 0
}

/** Call when the user accepted the reload, so we can stop asking if it never helps. */
export function record_reload(storage: SessionStorage = safe_session_storage) {
    const now = Date.now()
    const count = reloads_within_window(storage, now) + 1
    storage.setItem(RELOAD_BUDGET_KEY, JSON.stringify({count, at: now}))
}

export function init({
    pending_patches,
    on_worker_unavailable,
    on_worker_available,
    is_visible = () => document.visibilityState === "visible",
    storage = safe_session_storage,
}: {
    /** Decides how long we wait. A dead worker stops incoming updates even with nothing pending. */
    pending_patches: () => number
    on_worker_unavailable: () => void
    /** Called when a worker we had given up on answers after all. */
    on_worker_available: () => void
    is_visible?: () => boolean
    storage?: SessionStorage
}) {
    let missed = 0
    let last_pong_at = Date.now()
    let prompted = false

    /** `since_last_pong` says how long the worker had been silent when we noticed. */
    function diagnostics() {
        return {
            missed,
            since_last_pong: Date.now() - last_pong_at,
            pending_patches: pending_patches(),
        }
    }

    return {
        /** Call whenever the worker answered. */
        pong: () => {
            if (prompted) {
                // It was only slow, so take the prompt back and start watching again.
                report_info("[worker_liveness] The worker answered after all", diagnostics())
                prompted = false
                on_worker_available()
            }
            missed = 0
            last_pong_at = Date.now()
        },
        /**
         * Call when the page became visible again. The tick before the page was hidden belongs to
         * a different era and must not count against the worker.
         */
        resume: () => {
            missed = 0
        },
        /** Call once per watchdog interval, before the next ping goes out. */
        tick: () => {
            if (!is_visible()) {
                // A hidden page does not run its timers reliably, so a missing pong proves nothing.
                missed = 0
                return
            }
            missed += 1
            if (prompted || missed < MISSED_TICKS_WITH_PENDING_PATCHES) {
                return
            }
            const diagnosis = diagnostics()
            if (diagnosis.pending_patches === 0 && missed < MISSED_TICKS_UNTIL_UNAVAILABLE) {
                return
            }
            prompted = true
            report_error("[worker_liveness] The worker is gone", diagnosis)
            if (reloads_within_window(storage, Date.now()) >= RELOAD_BUDGET) {
                // Reloading did not help the last few times, so stop asking.
                report_info("[worker_liveness] Not asking to reload again", diagnosis)
                return
            }
            report_error(
                "[worker_liveness] The user was shown the 'Cling is no longer up to date' " +
                    "snackbar and asked to reload",
                diagnosis,
            )
            on_worker_unavailable()
        },
    }
}
